import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { ChopPunktekategorie } from '@entity/chop';
import { ChopService } from '@service/chop.service';
import { FormButtonBarComponent } from '@shared/form-button-bar/form-button-bar.component';
import { FormNumberInputComponent } from '@shared/form-number-input/form-number-input.component';
import { FormTextInputComponent } from '@shared/form-text-input/form-text-input.component';
import { applyChopServerValidationError } from '../../../chop-server-errors';

const FIELD_MAP = { Code: 'code', 'Bezeichnung.De': 'bezeichnung.De' } as const;

@Component({
  selector: 'app-chop-punktekategorie-dialog',
  templateUrl: './chop-punktekategorie-dialog.component.html',
  styleUrls: ['./chop-punktekategorie-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ReactiveFormsModule, FormTextInputComponent, FormNumberInputComponent, FormButtonBarComponent],
})
export class ChopPunktekategorieDialogComponent {
  private formBuilder = inject(FormBuilder);
  private dialogConfig = inject(DynamicDialogConfig);
  private dialogRef = inject(DynamicDialogRef);
  private chopService = inject(ChopService);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);

  private alterskategorieId: number = this.dialogConfig.data.alterskategorieId;
  private punktekategorie: ChopPunktekategorie | undefined = this.dialogConfig.data.punktekategorie;
  form = this.formBuilder.nonNullable.group({
    code: [this.punktekategorie?.code ?? '', Validators.required],
    bezeichnung: this.formBuilder.nonNullable.group({
      De: [this.punktekategorie?.bezeichnung?.De ?? '', Validators.required],
      Fr: [this.punktekategorie?.bezeichnung?.Fr ?? ''],
      It: [this.punktekategorie?.bezeichnung?.It ?? ''],
    }),
    punkteVon: [this.punktekategorie?.punkteVon ?? 0, Validators.required],
    punkteBis: [this.punktekategorie?.punkteBis as number | null],
    betrag: [this.punktekategorie?.betrag as number | null],
  });
  submitting = false;

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting = true;
    const value = this.form.getRawValue() as ChopPunktekategorie;
    const action = this.punktekategorie
      ? this.chopService.updatePunktekategorie(this.punktekategorie.id, { ...this.punktekategorie, ...value })
      : this.chopService.createPunktekategorie(this.alterskategorieId, value);
    action.pipe(finalize(() => (this.submitting = false))).subscribe({
      next: (result) => this.dialogRef.close(result),
      error: (err) => {
        if (!applyChopServerValidationError(this.form, err?.error, FIELD_MAP)) {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('GLOBAL.NOT_SUCCESSFULLY_SAVED'),
            detail: err?.error?.message,
          });
        }
      },
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
